import { apiRoutes, getV2, postV2 } from './apiRoutes'
import config from '../config/vue.config'
import i18n from '@/i18n'
import { getViewerToken, getWebappToken } from '@/utils/token'
import * as schema from './schema/appGatewayServer'
import { GachaListResponse } from './pb/Gacha/GachaListResponse'
import {
  FurnitureGachaGetItemListRequest,
  FurnitureGachaGetItemListResponse,
} from './pb/FurnitureGacha/FurnitureGachaGetItemList'
import { EmptyResponse } from './pb/Common/Empty'
import { getDestinationHeader } from '@/utils/api_destination_header'
import { WiseAccountGetResponse } from './pb/WiseAccount/WiseAccountGet'
import { WisePayoutListResponse } from './pb/Wise/WisePayoutList'
import { WiseRateGetResponse } from './pb/Wise/WiseRateGet'
import { WiseInServiceRegionResponse } from './pb/Wise/WiseInServiceRegion'
import {
  GachaGetEmissionItemListRequest,
  GachaGetEmissionItemListResponse,
} from './pb/Gacha/GachaGetEmissionItemList'
import {
  HistoryAlbumGetRequest,
  HistoryAlbumGetResponse,
} from './pb/HistoryAlbum/HistoryAlbumGet'
import {
  WebUserFeedbackStatusGetRequest,
  WebUserFeedbackStatusGetResponse,
} from './pb/WebUserFeedback/WebUserFeedbackStatusGet'
import { WebUserFeedbackStatusUpdateRequest } from './pb/WebUserFeedback/WebUserFeedbackStatusUpdate'
import {
  AgencyEventRankingGetEventRequest,
  AgencyEventRankingGetEventResponse,
} from './pb/AgencyEventRanking/AgencyEventRankingGetEvent'
import {
  AgencyEventRankingGetIndividualRequest,
  AgencyEventRankingGetIndividualResponse,
} from './pb/AgencyEventRanking/AgencyEventRankingGetIndividual'
import {
  AgencyEventRankingGetGroupsRequest,
  AgencyEventRankingGetGroupsResponse,
} from './pb/AgencyEventRanking/AgencyEventRankingGetGroups'
import {
  AgencyEventRankingGetGroupIndividualRequest,
  AgencyEventRankingGetGroupIndividualResponse,
} from './pb/AgencyEventRanking/AgencyEventRankingGetGroupIndividual'
import { Live2dModelUploadUrlResponse } from '@/api/pb/Live2d/Live2dModelUploadUrl'
import { Live2dCanUseResponse } from '@/api/pb/Live2d/Live2dCanUse'
import { Live2dModelUploadStatusResponse } from '@/api/pb/Live2d/Live2dModelUploadStatus'

const defaultHeaders = {
  Authorization: undefined as string | undefined,
  'Accept-Language': i18n.locale,
  'X-VLIVE-CLIENT-TYPE': 'webapp',
  'Content-Type': 'application/json',
  Accept: 'application/x-protobuf',
  ...getDestinationHeader(),
}

const tree = {
  gacha: {
    list: getV2<schema.ApiGachaListReq, GachaListResponse>(GachaListResponse),
    emissionItem: {
      list: postV2<
        GachaGetEmissionItemListRequest,
        GachaGetEmissionItemListResponse
      >(GachaGetEmissionItemListResponse),
    },
  },
  furnitureGacha: {
    // furniture_gacha/get_item_list
    getItemList: postV2<
      FurnitureGachaGetItemListRequest,
      FurnitureGachaGetItemListResponse
    >(FurnitureGachaGetItemListResponse),
  },
  wiseAccount: {
    // wise_account/get
    get: postV2<WiseAccountGetResponse>(WiseAccountGetResponse),
    store: postV2<schema.WiseAccountStoreParam, EmptyResponse>(EmptyResponse),
  },
  wise: {
    // wise/in_service_region
    inServiceRegion: postV2<WiseInServiceRegionResponse>(
      WiseInServiceRegionResponse
    ),
    rate: {
      // wise/rate/get
      get: postV2<WiseRateGetResponse>(WiseRateGetResponse),
    },
    payout: {
      // wise/payout/request
      request: postV2<
        {
          AmountOfLivePoints: number
          RateOfUSD: string
          PreferredCurrency: string
        },
        EmptyResponse
      >(EmptyResponse),
      // wise/payout/list
      list: postV2<WisePayoutListResponse>(WisePayoutListResponse),
    },
  },
  musicRights: {
    // music_rights/apply
    apply: postV2<schema.MusicRightsStoreParam, EmptyResponse>(EmptyResponse),
  },
  historyAlbum: {
    // history_album/get
    get: postV2<HistoryAlbumGetRequest, HistoryAlbumGetResponse>(
      HistoryAlbumGetResponse
    ),
  },
  agencyEvent: {
    ranking: {
      // agency_event/ranking/get_event
      getEvent: postV2<
        AgencyEventRankingGetEventRequest,
        AgencyEventRankingGetEventResponse
      >(AgencyEventRankingGetEventResponse),
      // agency_event/ranking/get_individual
      getIndividual: postV2<
        AgencyEventRankingGetIndividualRequest,
        AgencyEventRankingGetIndividualResponse
      >(AgencyEventRankingGetIndividualResponse),
      // agency_event/ranking/get_groups
      getGroups: postV2<
        AgencyEventRankingGetGroupsRequest,
        AgencyEventRankingGetGroupsResponse
      >(AgencyEventRankingGetGroupsResponse),
      group: {
        // agency_event/ranking/group/get_individual
        getIndividual: postV2<
          AgencyEventRankingGetGroupIndividualRequest,
          AgencyEventRankingGetGroupIndividualResponse
        >(AgencyEventRankingGetGroupIndividualResponse),
      },
    },
  },
  webUserFeedback: {
    status: {
      get: postV2<
        WebUserFeedbackStatusGetRequest,
        WebUserFeedbackStatusGetResponse
      >(WebUserFeedbackStatusGetResponse),
      update: postV2<WebUserFeedbackStatusUpdateRequest, EmptyResponse>(
        EmptyResponse
      ),
    },
  },
  live2d: {
    model: {
      uploadUrl: getV2<Live2dModelUploadUrlResponse>(
        Live2dModelUploadUrlResponse
      ),
      uploadStatus: getV2<Live2dModelUploadStatusResponse>(
        Live2dModelUploadStatusResponse
      ),
    },
    canUse: getV2<Live2dCanUseResponse>(Live2dCanUseResponse),
  },
}

const routePath = apiRoutes(
  `${config.appGatewayServerUrl}/api/v2`,
  defaultHeaders
)(tree)

export default routePath

// 実行時に各種ヘッダをセットするラッパー
export const apiV2 = () => {
  const viewerToken = getViewerToken()
  const webappToken = getWebappToken()

  if (viewerToken) {
    defaultHeaders.Authorization = `Bearer ${viewerToken}`
  } else if (webappToken) {
    defaultHeaders.Authorization = `Bearer ${webappToken}`
  }

  return routePath
}
